// EnvironmentBanner — shown at the top of the super-admin content area when
// the API is NOT in production mode, any background job is switched off, or
// outbound messaging is not "live". It is the visible half of the server's
// job kill switches (JOBS_ENABLED / JOB_<NAME>_ENABLED) and OUTBOUND_MODE.
// Renders nothing while loading, on error, or when everything is live.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import styles from './EnvironmentBanner.module.css';

export interface OpsJob {
  name: string;
  enabled: boolean;
  reason: string;
}

export interface OpsStatus {
  production_mode: boolean;
  node_env: string;
  app_env: string | null;
  jobs: OpsJob[];
  outbound?: {
    mode: 'live' | 'log_only' | 'redirect' | 'allowlist';
    redirect_email: string | null;
    redirect_sms: string | null;
    allowlist_count: number;
    production_refused: boolean;
  };
}

export function useOpsStatus() {
  return useQuery<OpsStatus>({
    queryKey: ['super', 'system', 'jobs'],
    queryFn: async () => {
      const res = await apiClient.get<OpsStatus>('/super/system/jobs');
      return res.data;
    },
    refetchInterval: 60_000,
    staleTime: 50_000,
    retry: false,
  });
}

function outboundText(o: NonNullable<OpsStatus['outbound']>): string | null {
  switch (o.mode) {
    case 'live':
      return null;
    case 'log_only':
      return 'Outbound messages: LOG ONLY (nothing is actually sent).';
    case 'redirect':
      return `Outbound messages: REDIRECTED to ${o.redirect_email ?? 'no test email'} / ${o.redirect_sms ?? 'no test number'}.`;
    case 'allowlist':
      return `Outbound messages: ALLOWLIST only (${o.allowlist_count} permitted address/number entries).`;
  }
}

export function EnvironmentBanner(): JSX.Element | null {
  const { data } = useOpsStatus();
  if (data === undefined) return null;

  const disabled = data.jobs.filter((j) => !j.enabled);
  const lines: string[] = [];
  if (!data.production_mode) {
    lines.push(
      `Not in production mode (NODE_ENV=${data.node_env}, APP_ENV=${data.app_env ?? 'unset'}).`,
    );
  }
  if (disabled.length > 0) {
    lines.push(
      disabled.length === data.jobs.length
        ? 'All background jobs are OFF (no reminders, notifications, syncs or cleanups run).'
        : `Background jobs OFF: ${disabled.map((j) => j.name).join(', ')}.`,
    );
  }
  if (data.outbound !== undefined) {
    const t = outboundText(data.outbound);
    if (t !== null) lines.push(t);
    if (data.outbound.production_refused) {
      lines.push('Production is refusing to send: OUTBOUND_MODE is not set to live.');
    }
  }
  if (lines.length === 0) return null;

  return (
    <div className={styles.banner} role="status" data-testid="environment-banner">
      <strong>Safe mode</strong>
      <ul className={styles.list}>
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </div>
  );
}
